import React, { useState, useRef, useCallback, useEffect } from 'react';

const PRESETS = [
  { label: 'S', value: 200 },
  { label: 'M', value: 350 },
  { label: 'L', value: 500 },
  { label: '100%', value: '100%' },
];

const ResizableImage = ({ block, contentState, blockProps }) => {
  const entityKey = block.getEntityAt(0);
  const entity = entityKey ? contentState.getEntity(entityKey) : null;
  const entityData = entity ? entity.getData() : {};
  const { src, alt, width } = entityData;

  const { onResize } = blockProps;
  const imgRef = useRef(null);

  const isFullWidth = width === '100%';
  const [currentWidth, setCurrentWidth] = useState(isFullWidth ? '100%' : (parseInt(width) || 350));
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // If currently 100%, measure actual pixel width to start drag from
    const actualWidth = currentWidth === '100%'
      ? (imgRef.current ? imgRef.current.offsetWidth : 350)
      : currentWidth;
    setDragging(true);
    startX.current = e.clientX;
    startWidth.current = actualWidth;
    if (currentWidth === '100%') setCurrentWidth(actualWidth);
  }, [currentWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, Math.min(800, startWidth.current + diff));
    setCurrentWidth(newWidth);
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    if (onResize && entityKey) {
      onResize(entityKey, currentWidth);
    }
  }, [dragging, entityKey, currentWidth, onResize]);

  const handlePreset = useCallback((value) => {
    setCurrentWidth(value);
    if (onResize && entityKey) {
      onResize(entityKey, value);
    }
  }, [onResize, entityKey]);

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (imgRef.current && !imgRef.current.contains(e.target)) {
        setSelected(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!entityKey || !src) return null;

  const widthStyle = currentWidth === '100%' ? '100%' : `${currentWidth}px`;
  const widthLabel = currentWidth === '100%' ? '100%' : `${currentWidth}px`;
  const isActive = (val) => {
    if (val === '100%') return currentWidth === '100%';
    return currentWidth === val;
  };

  return (
    <div
      ref={imgRef}
      onClick={(e) => { e.stopPropagation(); setSelected(true); }}
      style={{
        display: 'inline-block',
        position: 'relative',
        width: widthStyle,
        maxWidth: '100%',
        cursor: 'default',
        border: selected ? '2px solid #5e72e4' : '2px solid transparent',
        borderRadius: '4px',
        padding: '2px',
      }}
    >
      <img
        src={src}
        alt={alt || ''}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
      {selected && (
        <>
          {/* Preset size buttons */}
          <div
            style={{
              position: 'absolute',
              top: -30,
              left: 0,
              display: 'flex',
              gap: '2px',
              zIndex: 10,
            }}
          >
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreset(p.value); }}
                style={{
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  backgroundColor: isActive(p.value) ? '#5e72e4' : '#e9ecef',
                  color: isActive(p.value) ? '#fff' : '#333',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Width label */}
          <div
            style={{
              position: 'absolute',
              top: -30,
              right: 0,
              backgroundColor: '#5e72e4',
              color: '#fff',
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '3px',
              pointerEvents: 'none',
            }}
          >
            {widthLabel}
          </div>

          {/* Bottom-right resize handle */}
          <div
            onMouseDown={handleMouseDown}
            style={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              width: 14,
              height: 14,
              backgroundColor: '#5e72e4',
              border: '2px solid #fff',
              borderRadius: '2px',
              cursor: 'nwse-resize',
              zIndex: 10,
            }}
          />
        </>
      )}
    </div>
  );
};

export default ResizableImage;
